
import { mapGetters, mapState } from 'vuex'

import BreadcrumbsNavigation from '@/components/BreadcrumbsNavigation'
import ReviewStars from '@/components/ReviewStars'
import ImageCarousel from '@/components/ImageCarousel'
import ProductPrescription from '@/components/ProductPrescription'
import AccordionContainer from '@/components/AccordionContainer'
import ProductReviews from '@/components/ProductReviews'
import AuthorBlock from '@/components/AuthorBlock'
import RecommendationCarousel from '@/components/RecommendationCarousel'
import ModalDialogRecommendations from '@/components/ModalDialogRecommendations'
import DropdownSelector from '@/components/DropdownSelector'
import LoadingSpinner from '@/components/LoadingSpinner'

import slugifyMixin from '@/mixins/slugify'
import { postageDocumentId, productTypes } from '@/utils/constants'

export default {
  jsonld() {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Product',
          name: this.product.name,
          ...(this.product.images.length && { image: `${this.cdn}/90/${this.product.images[0]?.path}` }),
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: this.product.reviewStars.toString(),
            ratingCount: '',
            reviewCount: this.product.reviewsTotal.toString(),
            bestRating: '5',
            worstRating: '1',
          },
          review: this.product.reviews.map((review) => ({
            '@type': 'Review',
            author: {
              '@type': 'Person',
              name: review.reviewer,
            },
            datePublished: review.createdAt,
          })),
          offers: {
            '@type': 'Offer',
            url: `https://${this.hostUrl}${this.$nuxt.$route.path}`,
            priceCurrency: this.$i18n.localeProperties.currencyCode,
            price: this.product?.discountedPrice || this.product?.fullPrice,
            priceValidUntil: (() => {
              const d = new Date()
              d.setFullYear(d.getFullYear() + 30)
              return d.toLocaleDateString()
            })(),
            availability: this.product?.meta?.markupAvailability,
            itemCondition: 'NewCondition',
          },
          brand: this.product?.brand?.name,
          sku: this.product.name,
          gtin13: this.product.gtin13,
          category: [this.product.catName],
          productID: this.product.code,
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: this.product.breadcrumb?.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@id':
                item.link === null
                  ? `https://${this.hostUrl}${this.$nuxt.$route.path}`
                  : `https://${this.hostUrl}${item.link}`,
              name: item.name,
            },
          })),
        },
      ],
    }
  },
  components: {
    BreadcrumbsNavigation,
    ReviewStars,
    ImageCarousel,
    ProductPrescription,
    AccordionContainer,
    ProductReviews,
    AuthorBlock,
    RecommendationCarousel,
    ModalDialogRecommendations,
    DropdownSelector,
    LoadingSpinner,
  },
  mixins: [slugifyMixin],
  asyncData({ req }) {
    return { hostUrl: req?.headers?.host || window.location.host }
  },
  data() {
    const { content } = this.$store.state.product

    return {
      activeAccordion: content.length > 0 ? content[0].label : '',
      showUpsellDialog: false,
      productPageUpsell: false,
      pricePerLensToggle: false,
      packSizeSwitcherToggle: false,
      packSizeSavingsToggle: false,
    }
  },
  async fetch({ store, route, app }) {
    const id = app.$getResourceId(route.name)

    store.dispatch('product/setProductId', id)
    store.dispatch('stock/resetStock')

    await Promise.all([store.dispatch('product/getProduct'), store.dispatch('product/getRecommendations')])
    await store.dispatch('product/getReviews')

    store.dispatch('prescription/setDefaultOptions')

    if (!store.state.product.disabled) {
      await store.dispatch('stock/getStock')
    }

    await store.dispatch('stock/getGuesstimation')
  },
  head() {
    const langMeta =
      this.product?.meta?.lang?.map((lang) => ({
        rel: 'alternate',
        hreflang: lang.code,
        href: lang.url,
      })) || []
    const canonical = this.product?.meta?.canonicalUrl
      ? {
          rel: 'canonical',
          href: `https://${this.hostUrl}/${this.product.meta.canonicalUrl}`,
          hid: 'canonical',
        }
      : {}
    return {
      title: this.product.meta?.title || this.meta?.title,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.product.meta?.description || this.meta?.description,
        },
      ],
      link: [canonical, ...langMeta],
    }
  },
  computed: {
    ...mapGetters('prescription', ['isFilled', 'optionsStrings', 'quantityLimit']),
    ...mapState(['loading', 'errors', 'product', 'prescription', 'cdn', 'meta', 'couponCode']),
    ...mapState({
      stock: (state) => ({
        leftEye: {
          isLoading: !!state.loading.leftEye,
          error: state.errors.leftEye,
          stock: state.stock.leftEyeStock?.stock,
          limited: state.stock.leftEyeStock?.limited,
        },
        rightEye: {
          isLoading: !!state.loading.rightEye,
          error: state.errors.rightEye,
          stock: state.stock.rightEyeStock?.stock,
          limited: state.stock.rightEyeStock?.limited,
        },
        accessory: {
          isLoading: !!state.loading.accessory,
          error: state.errors.accessory,
          stock: state.stock.accessoryStock?.stock,
          limited: state.stock.accessoryStock?.limited,
        },
      }),
      guesstimation: (state) => ({
        isLoading: !!state.loading.guesstimation,
        deliveryDateFrom: state.stock.deliveryDateFrom || null,
        deliveryDateTo: state.stock.deliveryDateTo || null,
        cutoff: state.stock.cutoff || null,
      }),
      productErrors: (state) => ({
        leftEye: state.prescription.leftEyeErrors,
        rightEye: state.prescription.rightEyeErrors,
        messages: state.prescription.warningMessages,
      }),
      selectedOptions: (state) => ({
        leftEye: {
          isSelected: state.prescription.isLeftEyeSelected,
          options: state.prescription.leftEyeOptions,
        },
        rightEye: {
          isSelected: state.prescription.isRightEyeSelected,
          options: state.prescription.rightEyeOptions,
        },
      }),
    }),
    postageLink() {
      return `/document/${postageDocumentId}`
    },
    isLoading() {
      const { product, reviews, recommendations } = this.loading

      return {
        product: !!product,
        reviews: !!reviews,
        recommendations: !!recommendations,
      }
    },
    hasErrors() {
      const { product, reviews, recommendations } = this.errors

      return {
        product: product || null,
        reviews: reviews || null,
        recommendations: recommendations || null,
      }
    },
    totalQuantity() {
      let totalQuantity = this.prescription.quantity
      if (this.product.options.length) {
        const selectedEyes = [this.selectedOptions.leftEye.isSelected, this.selectedOptions.rightEye.isSelected].filter(
          Boolean,
        ).length
        totalQuantity *= selectedEyes
      }
      return totalQuantity
    },
    shouldShowProductUpsell() {
      return this.productPageUpsell && !!this.product?.recommendations?.length
    },
    showPricePerLens() {
      return (
        this.pricePerLensToggle &&
        this.product?.productType === productTypes.CONTACT_LENS &&
        !!this.product?.pricePerUnit
      )
    },
    showPackSizeSwitcher() {
      return this.packSizeSwitcherToggle && !!this.product?.upgradePath?.products?.length
    },
    upgradePathOptions() {
      const options = []
      this.product?.upgradePath?.products?.forEach((product) => {
        const option = {
          label: product.packSize,
          value: product.id,
        }
        options.push(option)
      })
      return options
    },
    // Return all items in upgradePath products list which have a larger pack size || empty array
    largerPackSizes() {
      if (!this.product.units || !this.product.upgradePath?.products?.length) {
        return []
      }

      return this.product.upgradePath.products.filter((product) => {
        return !!product.units && this.product.units < product.units
      })
    },
    // Return the largest item in largerPackSizes which also has a lower pricePerUnit || null
    largestPackSizeWithSaving() {
      if (!this.product.pricePerUnit) {
        return null
      }

      const mainProductPricePerUnit = this.product.pricePerUnit
      return this.largerPackSizes.reduce((best, current) => {
        const isCheaperThanMainProduct = current.pricePerUnit < mainProductPricePerUnit
        const isLargerPackSizeThanCurrentBest = !best || current.units > best.units

        return !!current.pricePerUnit && isCheaperThanMainProduct && isLargerPackSizeThanCurrentBest ? current : best
      }, null)
    },
    // Difference in pricePerUnit between current product and largest pack size with saving product || 0
    largestPackSizeSavingAmount() {
      if (!this.product.pricePerUnit || !this.largestPackSizeWithSaving?.pricePerUnit) {
        return 0
      }

      const saving = this.product.pricePerUnit - this.largestPackSizeWithSaving.pricePerUnit
      return Math.round((saving + Number.EPSILON) * 100) / 100
    },
    showPackSizeSavings() {
      return !!this.packSizeSavingsToggle && !!this.largestPackSizeWithSaving && this.largestPackSizeSavingAmount > 0
    },
  },
  watch: {
    selectedOptions() {
      if (this.isFilled) {
        this.logPrescriptionEntered()
      }
    },
  },
  async mounted() {
    const { packSize, code, name, fullPrice, discountedPrice, catName } = this.product
    this.productPageUpsell = await this.$store.dispatch('split/getSplitTreatment', 'productPageUpsell')
    this.pricePerLensToggle = await this.$store.dispatch('split/getSplitTreatment', 'productPageShowPricePerLens')
    this.packSizeSwitcherToggle = await this.$store.dispatch('split/getSplitTreatment', 'productPagePackSizeSwitcher')
    this.packSizeSavingsToggle = await this.$store.dispatch('split/getTreatmentWithoutStoring', {
      splitName: 'productPagePackSizeSavings',
      attributes: {
        couponCode: this.couponCode,
      },
    })

    const increaseQuantityToTwo = await this.$store.dispatch('split/getTreatmentWithoutStoring', {
      splitName: 'productPageSetQuantityToTwo',
      attributes: {
        productId: this.product.id,
        durationDays: this.product.durationDays,
        modality: this.product.modality,
        couponCode: this.couponCode,
      },
    })

    if (increaseQuantityToTwo) {
      this.updateQuantity(2)
    }

    this.$gtm.push({ ecommerce: null })
    this.$gtm.push({
      event: 'productView',
      ecommerce: {
        currencyCode: 'GBP',
        detail: {
          products: [
            {
              brand: '',
              category: '',
              currency: 'GBP',
              modality: '',
              packsize: packSize,
              id: code,
              name,
              price: discountedPrice || fullPrice,
              subcategory: catName,
              url: window.location.href,
              variant: '',
            },
          ],
        },
      },
    })

    // Initial prescription entry incomplete state
    this.logPrescriptionEntered()
  },
  methods: {
    onMobileReviewClick() {
      const y = this.$refs.mobileReviews.getBoundingClientRect().top + window.pageYOffset - 70

      window.scrollTo({ top: y, behavior: 'smooth' })
    },
    onDesktopReviewClick() {
      const y = this.$refs.desktopReviews.getBoundingClientRect().top + window.pageYOffset

      window.scrollTo({ top: y, behavior: 'smooth' })
    },
    productImageChanged() {},
    openHelp() {},
    toggleEye(eyeId, isChecked) {
      this.$store.dispatch('prescription/toggleEye', eyeId)
      this.$store.dispatch('stock/getGuesstimation')
    },
    async selectEyeOption(eye) {
      this.$store.dispatch('prescription/setEyeOption', eye)
      await this.$store.dispatch('stock/getStock')
      this.$store.dispatch('stock/getGuesstimation')
    },
    updateQuantity(quantity) {
      this.$store.dispatch('prescription/updateQuantity', quantity)
      this.$store.dispatch('stock/getGuesstimation')
    },
    async addToBasket() {
      await this.$store.dispatch('basket/addToBasket')

      if (this.isFilled) {
        const { packSize, code, name, fullPrice, discountedPrice, catName, link } = this.product

        const data = {
          event: 'addToCart',
          ecommerce: {
            currencyCode: 'GBP',
            add: {
              products: [
                {
                  brand: '',
                  category: '',
                  currency: 'GBP',
                  modality: '',
                  packsize: packSize,
                  id: code,
                  name,
                  price: discountedPrice || fullPrice,
                  quantity: this.totalQuantity,
                  subcategory: catName,
                  url: link,
                  variant: '',
                },
              ],
            },
          },
        }

        this.$gtm.push({ ecommerce: null })
        this.$gtm.push(data)

        if (this.shouldShowProductUpsell) {
          this.showProductUpsell()
          return
        }

        await this.$router.push(this.$getLink('/basket'))
      }
    },
    async addRecommendedToBasket(item, addToCartSource = 'productPage') {
      await this.$store.dispatch('basket/addRecommendedToBasket', item)

      const { name, discountPrice, price, productCode, link } = item

      const data = {
        event: 'addToCart',
        addToCartSource,
        ecommerce: {
          currencyCode: 'GBP',
          add: {
            products: [
              {
                brand: '',
                category: '',
                currency: 'GBP',
                modality: '',
                packsize: '',
                id: productCode,
                name,
                price: discountPrice || price,
                quantity: 1,
                subcategory: '',
                url: link,
                variant: '',
              },
            ],
          },
        },
      }

      this.$gtm.push({ ecommerce: null })
      this.$gtm.push(data)
    },
    toggleAccordion(activeAccordion) {
      this.activeAccordion = this.activeAccordion !== activeAccordion ? activeAccordion : ''
    },
    loadMoreReviews() {
      this.$store.dispatch('product/getReviews')
    },
    askOpticianClicked() {},
    logPrescriptionEntered() {
      const data = {
        event: 'prescriptionDetails',
        prescription: {
          enteredPrescription: this.isFilled && !!this.product.options.length,
        },
      }

      // Additional push for when a product with options is filled
      if (this.isFilled && this.product.options.length) {
        const prescriptionDetail = {}

        prescriptionDetail.leftEyeCheckbox = this.selectedOptions.leftEye.isSelected
        if (prescriptionDetail.leftEyeCheckbox) {
          prescriptionDetail.leftEyePrescription = this.optionsStrings.leftEye
        }

        prescriptionDetail.rightEyeCheckbox = this.selectedOptions.rightEye.isSelected
        if (prescriptionDetail.rightEyeCheckbox) {
          prescriptionDetail.rightEyePrescription = this.optionsStrings.rightEye
        }

        data.prescription = {
          ...data.prescription,
          ...prescriptionDetail,
        }
      }

      this.$gtm.push(data)
    },
    showProductUpsell() {
      this.showUpsellDialog = true
    },
    continueToBasket() {
      this.showUpsellDialog = false
      this.$router.push(this.$getLink('/basket'))
    },
    upgradePathClicked(productId) {
      if (this.product.id === productId) {
        return
      }
      this.logUpgradePathClicked(productId)
      this.$router.push(this.$getLink(`/product/${productId}`))
    },
    logUpgradePathClicked(productId) {
      const data = {
        event: 'productPagePackSizeSwitched',
        fromProductId: this.product.id,
        toProductId: productId,
      }
      this.$gtm.push(data)
    },
  },
}
